import { useFeature } from '@/features/feature-flags';
import { FEATURES } from '@/features/feature-flags/feature-flags.constants';

import { usePeopleWikiQuery } from './people-wiki.query';

import type { WikiPerson } from './people-wiki.types';

/** The user's People Wiki page for a Littlebird user, which messages name by id. Undefined when the user has no page
 * for them, or cannot message them any more. */
export const wikiPersonOfUser = (
  people: readonly WikiPerson[] | undefined,
  userId: number
): WikiPerson | undefined =>
  people?.find(({ littlebirdAccounts }) =>
    littlebirdAccounts.some((account) => account.userId === userId)
  );

/** The shared source of a person's name and photo in messages: the user's own People Wiki page for them, so that a
 * message shows who the user knows, and not the name on the other person's account. Returns a lookup by user id, for a
 * caller that reads several people. */
export const useWikiPeopleOfUsers = (): ((
  userId: number
) => WikiPerson | undefined) => {
  const hasFeature = useFeature();
  const enabled = hasFeature(FEATURES.PEOPLE_WIKI);
  const { data } = usePeopleWikiQuery({ enabled });
  // A disabled query keeps its cached people, which a turned-off feature must not show
  return (userId) => wikiPersonOfUser(enabled ? data : undefined, userId);
};

export const useWikiPersonOfUser = (userId: number): WikiPerson | undefined =>
  useWikiPeopleOfUsers()(userId);
